/* eslint-disable complexity */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { REPORT, ReportType, Reporter, UpdateReport } from 'src/api';
import axios from 'src/axios';
import { Checkbox, Switch } from 'src/components/atoms';
import { BodyText } from 'src/components/atomsLegacy';
import { handleErrorToast } from 'src/components/molecules';
import { ReportField } from 'src/components/moleculesLegacy';
import { fetchDraftReport, saveContactEmail } from 'src/creators';
import { useRelationshipTypeQuery } from 'src/graphql';
import { AppDispatch } from 'src/store';
import { SUBMISSION_MODE } from 'src/types';
import * as Validator from 'validator';
import {
  DetailContainer,
  IdentityContainer,
  Title,
} from './ReporterDetails.styles';

interface Props {
  reportID: string;
  currentReport?: UpdateReport | null;
  reporter?: Reporter;
  isDraftReport?: boolean;
  submissionMode?: number | null;
  isAnonymous?: boolean | null;
  updateReport?: (reportID: string, report: UpdateReport) => void;
  setIsAddingReporter?: (isAddingReporter: boolean) => void;
  reporterRelationshipType?: number | null;
  isUpdateRelationshipToOrganisationEnabled?: boolean;
}

const hiddenString = (submissionMode?: number | null) =>
  submissionMode && submissionMode === SUBMISSION_MODE.OPEN_REPORTING
    ? '--'
    : '**********';

const reporterValues = (key: keyof Reporter, reporter?: Reporter) => {
  if (!key) return '--';
  if (!reporter) return '--';
  if (!reporter[key]) return '--';
  if (reporter[key]) return `${reporter[key]}`;
  return '--';
};

export const ReporterDetails = ({
  reportID,
  currentReport,
  reporter,
  updateReport,
  setIsAddingReporter,
  isDraftReport = false,
  submissionMode,
  isAnonymous,
  reporterRelationshipType,
  isUpdateRelationshipToOrganisationEnabled,
}: Props) => {
  const [hidden, setHidden] = useState(
    currentReport?.anonymous ?? isAnonymous ?? false,
  );
  const [contactEmail, setContactEmail] = useState(
    reporterValues('contactEmail', reporter),
  );
  const dispatch = useDispatch<AppDispatch>();
  const reporterId = currentReport?.user || reporter?.id?.toString();
  const { data: relationshipTypes } = useRelationshipTypeQuery();

  const reporterRelationship = isUpdateRelationshipToOrganisationEnabled
    ? (relationshipTypes?.getRelationshipType.find(
        (type) => type.id === reporterRelationshipType,
      )?.name ?? '--')
    : reporterValues('relationshipToOrganisation', reporter);

  const fetchContactEmail = async () => {
    const result = await axios.get(REPORT(reportID));
    const report: ReportType = result.data.data;
    const reportUser = report.pk?.User ?? report.Reporter;
    const newContactEmail = reportUser?.contactEmail;
    setContactEmail(newContactEmail ?? '--');
  };

  const updateContactEmail = async (email: string) => {
    if (!Validator.isEmail(email)) {
      handleErrorToast({ toastMessage: 'Please enter a valid email' });
    } else {
      await dispatch(saveContactEmail(reporterId, email));
      isDraftReport
        ? await dispatch(fetchDraftReport(reportID))
        : await fetchContactEmail();
    }
  };

  const reporterContactEmail = reporterValues('contactEmail', reporter);
  useEffect(() => {
    setContactEmail(reporterContactEmail);
  }, [reporterContactEmail]);

  const setAnonymous = async () => {
    if (updateReport) {
      await updateReport(reportID, {
        ...currentReport,
        anonymous: !currentReport?.anonymous,
      });
    }
  };

  useEffect(() => {
    setHidden(currentReport?.anonymous ?? isAnonymous ?? false);
  }, [currentReport?.anonymous, isAnonymous]);

  const showExtendedUserDetail =
    (isDraftReport && currentReport?.User?.eid) || !isDraftReport;
  return (
    <>
      <div>
        <ReportField
          title="Reporter Name"
          value={reporterValues('name', reporter) satisfies string}
          hidden={hidden}
          hiddenString={hiddenString(submissionMode)}
          data-testid="reporter-name"
          editable={isDraftReport}
          onEditClick={
            setIsAddingReporter ? () => setIsAddingReporter(true) : undefined
          }
        />
        <ReportField
          title="Reporter Email"
          value={reporterValues('email', reporter)}
          hidden={hidden}
          hiddenString={hiddenString(submissionMode)}
          data-testid="reporter-email"
        />
        <ReportField
          title="Notifications Email"
          value={contactEmail || '--'}
          hidden={hidden}
          hiddenString={hiddenString(submissionMode)}
          data-testid="reporter-notifications-email"
          editable={submissionMode !== SUBMISSION_MODE.OPEN_REPORTING}
          onSave={updateContactEmail}
        />
        {showExtendedUserDetail ? (
          <>
            <ReportField
              title="Reporter Role"
              value={reporterValues('role', reporter)}
              hidden={hidden}
              hiddenString={hiddenString(submissionMode)}
              data-testid="reporter-role"
            />
            <ReportField
              title="Employee ID"
              value={reporterValues('eid', reporter)}
              hidden={hidden}
              hiddenString={hiddenString(submissionMode)}
              data-testid="reporter-eid"
            />
          </>
        ) : null}
        {showExtendedUserDetail || isUpdateRelationshipToOrganisationEnabled ? (
          <ReportField
            title="Relationship to Organisation"
            value={reporterRelationship}
            hidden={false}
            hiddenString={hiddenString(submissionMode)}
            data-testid="reporter-relationshipToOrganisation"
          />
        ) : null}
        {isDraftReport && (
          <DetailContainer data-testid="reporter-anonymous">
            <Title>Identity</Title>
            <IdentityContainer>
              <BodyText weight="regular">
                Would the reporter like to remain anonymous?
              </BodyText>
              <Checkbox
                data-testid="reporter-anonymous-checkbox"
                checked={currentReport?.anonymous ?? false}
                onChange={setAnonymous}
              />
            </IdentityContainer>
          </DetailContainer>
        )}
      </div>
      {isDraftReport && (
        <div style={{ paddingTop: '30px' }}>
          <Switch
            onClick={setHidden}
            label="Hide details"
            labelPosition="left"
            checked={hidden}
            data-testid={
              hidden ? 'show-reporter-details' : 'hide-reporter-details'
            }
          />
        </div>
      )}
    </>
  );
};
